import GridOnIcon from '@mui/icons-material/GridOn';
import ListIcon from '@mui/icons-material/List';
import TimelineIcon from '@mui/icons-material/Timeline';
import { Grid, IconButton } from "@mui/material";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css"; // ← ポイント！
import { AgGridReact } from "ag-grid-react";
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import { PageData } from '../../../models';
import { AG_GRID_LOCALE_JP } from '../../../theme/localeJP';
import LoadingOverlay, { DefaultLoadingMessage } from '../../common/LoadingOverlay';
import PaginationCpn from '../../common/PaginationCpn';
import NoRowsOverlay, { NoRowsMessage } from '../../common/NoRowsOverlay';
import { customerLabelName } from '../../../statics';
import { cellDeviceTypeNameRenderer } from '../../../utils';

// -------------
// Props
export type Ga2GridProps = {
    colState: any[];
    pageSize: number;
    pageData: PageData;
    onChangeSearchCondition: (name: string, value: any) => void;
    onShowDetails: (value: string, redirectId: string) => void;
    keyword: string;
    openSearch: boolean;
    authRole:any;
};

// -------------
// Component
export const Ga2Grid = forwardRef((props: Ga2GridProps, ref) => {
    const { colState, pageSize, pageData, keyword, openSearch, authRole, onChangeSearchCondition, onShowDetails,  } = props;

    const gridRef = useRef() as any;
    const [rowData, setRowData] = useState();

    useImperativeHandle(ref, () => ({
        saveState() {
            if (gridRef && gridRef.current && gridRef.current.columnApi) {
                return gridRef.current.columnApi.getColumnState();
            }
            return null;
        },

        setRowData(value: any) {
            setRowData(value)
        },

        hideOverlay() {
            gridRef.current.api.hideOverlay();
        }
    }));

    // Loading時のメッセージ
    const loadingOverlayComponent = useMemo(() => {
        return LoadingOverlay;
    }, []);
    const loadingOverlayComponentParams = useMemo(() => {
        return {
            loadingMessage: DefaultLoadingMessage,
        };
    }, []);

    const noRowsOverlayComponent = useMemo(() => {
        return NoRowsOverlay;
    }, []);

    const noRowsOverlayComponentParams = useMemo(() => {
        return {
            noRowsMessageFunc: () => NoRowsMessage,
        };
    }, []);

    // ag-Grid の準備完了後に呼ばれるコールバック関数
    const onGridReady = (grid: any) => {

    };

    // 編集ボタンのセル内表示とボタンイベント
    const cellMessageRenderer = (props: any) => {
        gridRef.current?.api?.setQuickFilter(keyword)
        // 詳細ボタンイベント
        const handleEdit = () => {
            onShowDetails(props.data, props.colDef.field);
        };

        return (
            <>
                {props.colDef.field === "data" &&
                    <Grid container direction="row" justifyContent="flex-start" paddingLeft={"-60px"} >
                        <IconButton aria-label="edit" style={{ color: 'white', backgroundColor: '#1890ff', borderRadius: '3px', margin: '4px' }} onClick={handleEdit} >
                            <ListIcon fontSize="small" />
                        </IconButton>
                    </Grid>}
                {props.colDef.field === "graph" &&
                    <Grid container direction="row" justifyContent="flex-start" paddingLeft={"-60px"}>
                        <IconButton aria-label="edit" style={{ color: 'white', backgroundColor: '#1890ff', borderRadius: '3px', margin: '4px' }} onClick={handleEdit} >
                            <TimelineIcon fontSize="small" />
                        </IconButton>
                    </Grid>}
                {props.colDef.field === "monthlyTable" &&
                    <Grid container direction="row" justifyContent="flex-start" paddingLeft={"-60px"}>
                        <IconButton aria-label="edit" style={{ color: 'white', backgroundColor: '#1890ff', borderRadius: '3px', margin: '4px' }} onClick={handleEdit} >
                            <GridOnIcon fontSize="small" />
                        </IconButton>
                    </Grid>}
                {props.colDef.field === "yearlyTable" &&
                    <Grid container direction="row" justifyContent="flex-start" paddingLeft={"-60px"}>
                        <IconButton aria-label="edit" style={{ color: 'white', backgroundColor: '#1890ff', borderRadius: '3px', margin: '4px' }} onClick={handleEdit} >
                            <GridOnIcon fontSize="small" />
                        </IconButton>
                    </Grid>}
            </>
        );
    };

    // 全列に共通の定義
    const defaultColDef = {
        sortable: true, // ソート可
        editable: false, // 編集不可
        resizable: true,
        suppressMovable: true,  // 列の移動を無効にする
    };

    // 列タイプの定義
    const columnTypes = {
        nonEditableColumn: { editable: false }, // 編集不可
    };

    // 列の個別定義
    const columnDefs = [
        {
            headerName: `${customerLabelName}名`,
            field: "customer_name",
            rowDrag: false,
            width: 400,
        },
        {
            headerName: "設置場所",
            field: "place",
            rowDrag: false,
            width: 300,
        },
        {
            headerName: "装置種別",
            field: "device_type_name",
            rowDrag: false,
            width: 120,
            valueGetter: (props:any) => {
                return cellDeviceTypeNameRenderer(props);
            }
        },
        {
            headerName: "装置ID",
            field: "id",
            rowDrag: false,
            width: 300,
        },
        {
            headerName: "CentralID",
            field: "centralid",
            rowDrag: false,
            width: 120,
        },
        {
            headerName: "定期通報",
            field: "data",
            cellRenderer: cellMessageRenderer,
            width: 200,
            sortable:false,
        },
        {
            headerName: "発電量",
            field: "graph",
            cellRenderer: cellMessageRenderer,
            width: 200,
            sortable:false,
        },
        {
            headerName: "月表",
            field: "monthlyTable",
            cellRenderer: cellMessageRenderer,
            width: 200,
            sortable:false,
        },
        {
            headerName: "年表",
            field: "yearlyTable",
            cellRenderer: cellMessageRenderer,
            width: 200,
            sortable:false,
        }
    ];

    if(authRole && authRole.base_view_flag == 1){
        columnDefs.splice(0, 0, {
            headerName: "拠点",
            field: "base_name",
            width: 100,
        } as any);
    }

    const rowHeight = 48;
    gridRef.current?.api?.setQuickFilter(keyword)

    return (
        <>
            <div>
                <div className="ag-theme-alpine" style={{ height: openSearch ? 'calc(100vh - 280px)' : 'calc(100vh - 240px)', width: "100%" }}>
                    <AgGridReact
                        ref={gridRef}
                        defaultColDef={defaultColDef}
                        columnTypes={columnTypes}
                        columnDefs={columnDefs}
                        onGridReady={onGridReady}
                        domLayout={"normal"}
                        rowData={rowData}
                        animateRows={true}
                        rowDragManaged={true}
                        suppressMovableColumns={false}
                        suppressDragLeaveHidesColumns={false}
                        rowHeight={rowHeight}
                        localeText={AG_GRID_LOCALE_JP}
                        paginationPageSize={pageSize}
                        cacheQuickFilter={true}
                        loadingOverlayComponent={loadingOverlayComponent}
                        loadingOverlayComponentParams={loadingOverlayComponentParams}
                        noRowsOverlayComponent={noRowsOverlayComponent}
                        noRowsOverlayComponentParams={noRowsOverlayComponentParams}
                    />
                </div>
                <PaginationCpn
                    pageData={pageData}
                    onChangeSearchCondition={onChangeSearchCondition}
                    limit={pageSize}
                    exportCsv={{ gridApi: gridRef?.current?.api, name: "太陽光発電一覧", columns: ['customer_name', 'place', 'id'] }}
                    enabledExportCsv={true}
                />
            </div>
        </>
    );
});

export default Ga2Grid;
